import { NavigateFunction } from "react-router-dom";
import SysColumn from "app/class/sys-column";
import {
    ClientActionBaseContext,
    executeClientAction,
} from "modules/record/shared/services/clientActionRuntime";
import { normalizeBooleanValue } from "modules/record/shared/fields/booleanValue";

const isScriptDefault = (column: SysColumn) =>
    String(column.default_value_type || "literal") === "script" &&
    Boolean(String(column.default_value_script || "").trim());

// Espelha o fallback determinístico do backend (`SysRecord.type_fallback_for`):
// sem `default_value` configurado, numérico vira `0`, não `""`.
const NUMERIC_COLUMN_TYPES = ["INTEGER", "DECIMAL", "CURRENCY"];

export const literalDefaultValueForColumn = (column: SysColumn) => {
    if (column.column_type === "BOOLEAN") {
        return normalizeBooleanValue(column.default_value);
    }

    const hasConfiguredDefault =
        column.default_value !== null && column.default_value !== undefined && column.default_value !== "";

    if (hasConfiguredDefault) {
        return column.default_value;
    }

    return NUMERIC_COLUMN_TYPES.includes(column.column_type) ? 0 : "";
};

export const buildLiteralDefaultValues = (columns: SysColumn[] = []) => {
    const values: Record<string, unknown> = {};

    columns.forEach((column) => {
        values[column.name] = literalDefaultValueForColumn(column);
    });

    return values;
};

export const applyDefaultValueScripts = async ({
    columns,
    baseValues,
    baseContext,
    table,
    tableName,
}: {
    columns: SysColumn[];
    baseValues: Record<string, unknown>;
    baseContext: ClientActionBaseContext;
    table: Record<string, unknown>;
    tableName: string;
}) => {
    const nextValues = { ...baseValues };

    for (const column of columns.filter(isScriptDefault)) {
        const result = await executeClientAction({
            script: String(column.default_value_script || ""),
            mode: "form",
            baseContext,
            current: nextValues,
            table,
            tableName,
            timeoutMs: column.default_value_timeout_ms,
            extraContext: {
                column,
                defaultValue: literalDefaultValueForColumn(column),
            },
        });

        nextValues[column.name] = result.returnValue;
    }

    return nextValues;
};

export const buildDefaultValueScriptBaseContext = ({
    notifyApi,
    navigate,
    userSession,
}: {
    notifyApi: {
        alert: (key: string, message: string) => void;
        warning: (key: string, message: string) => void;
        success: (key: string, message: string) => void;
        info: (key: string, message: string) => void;
    };
    navigate: NavigateFunction;
    userSession?: unknown;
}): ClientActionBaseContext => ({
    ...notifyApi,
    currentUser: userSession,
    navigate,
});
